import React from 'react'

import { SpectrophotometerChart } from '../Filter'
import RangeTablePrintout from './RangeTablePrintout'

import type { Filter } from '../../redux/state'

const SingleFilterPrintout = ({
  filter,
  selectedFrames
}: {
  filter: Filter,
  selectedFrames: string[]
}) => {
  const { availableFrames } = filter
  if (availableFrames == null) return null

  const frameImages: { image: string, frame: string }[] = selectedFrames
    .map(frame => ({ image: availableFrames[frame], frame }))
    .filter(x => x.image != null)
  const spectrophotometerMarginTop =
    filter.ods.length > filter.ceRatings.length ||
    (filter.ceRatings.length > 7 && frameImages.length > 0)
      ? '1em'
      : '2em'

  return (
    <div style={{ margin: '1.5em 10em' }}>
      <div style={styles.titleRow}>
        <div>
          <p style={{ fontSize: 20, marginBottom: '0.25em' }}>
            Product Specification
          </p>
          <h1 style={{ fontWeight: 700 }}>{filter.name}</h1>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p>
            {filter.ce && 'CE Certified'}
            <br />
            Luminous transmittance: {filter.vlt}% {filter.color}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', marginTop: '2em' }}>
        {filter.ceRatings.length > 0 &&
          <RangeTablePrintout
            first
            title="CE Rating"
            name="CE Ratings"
            items={filter.ceRatings}
          />
        }
        {filter.ods.length > 0 &&
          <RangeTablePrintout
            first={filter.ceRatings.length === 0}
            title="Optical Density"
            name="OD"
            items={filter.ods}
          />
        }
      </div>

      <div style={{ marginTop: spectrophotometerMarginTop }}>
        <h4>Spectrophotometer Data</h4>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <SpectrophotometerChart
            data={filter.spectrophotometerData}
            forPrint
            center
            width={600}
            height={225}
          />
        </div>
      </div>
      {frameImages.length > 0 && (
        <div
          style={{
            margin: '2em -4em',
            border: '1px solid rgb(206, 217, 224)',
            background: 'white',
            padding: 10
          }}
        >
          <div
            style={{
              display: 'grid',
              gridGap: 20,
              gridTemplateColumns: `repeat(${numberOfColumns(
                frameImages.length
              )}, 1fr)`
            }}
          >
            {frameImages.map(({ frame, image }) => (
              <div
                key={frame}
                style={{
                  alignSelf: 'end',
                  textAlign: 'center',
                  gridColumn: frameImages.length === 1 ? '1 / -1' : undefined
                }}
              >
                <img
                  src={image}
                  style={{
                    width: frameImages.length === 1 ? '45%' : '90%',
                    height: '100%',
                    maxHeight: frameImages.length > 2 ? '100px' : '200px',
                    objectFit: 'cover'
                  }}
                />
                <figcaption
                  style={{
                    textAlign: 'center',
                    marginTop: '0.5em'
                  }}
                >
                  {`#${frame.toUpperCase()}`}
                </figcaption>
              </div>
            ))}
          </div>
          <figcaption
            style={{
              textAlign: 'center',
              marginTop: '0.5em'
            }}
          >
            See our website for all frame options
          </figcaption>
        </div>
      )}
    </div>
  )
}

export default SingleFilterPrintout

const styles = {
  titleRow: {
    marginTop: '2rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end'
  }
}

const numberOfColumns = (numberOfFrames: number) => {
  if (numberOfFrames <= 2) return 2
  if (numberOfFrames <= 4) return numberOfFrames
  if (numberOfFrames <= 10) return Math.ceil(numberOfFrames / 2)
  return Math.ceil(numberOfFrames / 3)
}
